import { useState, useEffect } from 'react';
import { getPortals, createPortal, deletePortal, triggerPortalImport } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Globe, Plus, Trash2, Download, Loader2, Check, X, Sparkles, ChevronDown, ChevronRight, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import api from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PortalImportPage() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [portalForm, setPortalForm] = useState({ portal_name: 'StarGems', portal_url: 'https://custom.stargems.com', username: '', password: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Step 1: Order list
  const [step, setStep] = useState('portals'); // portals | list | details | importing
  const [fetching, setFetching] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [activePortalId, setActivePortalId] = useState(null);

  // Step 2: Detail scrape + preview
  const [scraping, setScraping] = useState(false);
  const [importSession, setImportSession] = useState(null);
  const [selectedForImport, setSelectedForImport] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingOrderIdx, setEditingOrderIdx] = useState(null);

  useEffect(() => { fetchPortals(); }, []);

  const fetchPortals = async () => {
    try { const res = await getPortals(); setPortals(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAddPortal = async (e) => {
    e.preventDefault();
    if (!portalForm.portal_name || !portalForm.username || !portalForm.password) { toast.error('Fill all required fields'); return; }
    setSaving(true);
    try {
      await createPortal(portalForm);
      toast.success('Portal added');
      setShowAddForm(false);
      setPortalForm({ portal_name: 'StarGems', portal_url: 'https://custom.stargems.com', username: '', password: '', notes: '' });
      fetchPortals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // STEP 1: Fetch open orders
  const handleFetchOrders = async (portalId) => {
    setActivePortalId(portalId);
    setFetching(true);
    setOrderList([]);
    setSelectedOrders(new Set());
    try {
      const res = await api.post(`${API}/portals/fetch-orders`, { portal_id: portalId, tabs: ["sketch_orders", "cad_request"] });
      setOrderList(res.data.orders || []);
      setStep('list');
      toast.success(`Fetched ${res.data.count} orders from SGI`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to fetch orders'); }
    finally { setFetching(false); }
  };

  const toggleOrder = (orderNo) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderNo)) next.delete(orderNo); else next.add(orderNo);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedOrders.size === orderList.length) setSelectedOrders(new Set());
    else setSelectedOrders(new Set(orderList.map(o => o.order_no)));
  };

  // STEP 2: Scrape details for selected orders
  const handleScrapeDetails = async () => {
    if (selectedOrders.size === 0) { toast.error('Select orders first'); return; }
    setScraping(true);
    try {
      const res = await api.post(`${API}/portals/scrape-details`, {
        portal_id: activePortalId,
        order_nos: Array.from(selectedOrders),
      });
      setImportSession(res.data);
      setSelectedForImport(new Set(res.data.orders?.map((_, i) => i) || []));
      setStep('details');
      // Auto-open first order for review/editing
      if (res.data.orders?.length > 0) {
        setEditingOrderIdx(0);
        setExpandedOrder(0);
      }
      toast.success(`Scraped ${res.data.orders?.length} order details — review below`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scrape failed'); }
    finally { setScraping(false); }
  };

  // STEP 3: Confirm import
  const handleConfirmImport = async () => {
    if (!importSession || selectedForImport.size === 0) return;
    setConfirming(true);
    try {
      const res = await api.post(`${API}/portals/confirm-import`, {
        import_id: importSession.id,
        selected_indices: Array.from(selectedForImport),
        orders: importSession.orders,
      });
      toast.success(`Imported ${res.data.count} orders as CAD tasks!`);
      setStep('portals');
      setImportSession(null);
      fetchPortals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setConfirming(false); }
  };

  return (
    <CollapsibleLayout>
      <div className="min-h-[calc(100vh-48px)] bg-slate-50">
        <header className="bg-slate-900 text-white px-6 py-3 sticky top-12 z-40">
          <div className="flex items-center justify-between max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3">
              <Globe size={20} className="text-teal-400" />
              <div>
                <h1 className="text-lg font-semibold" data-testid="portal-page-title">SGI Order Import</h1>
                <p className="text-slate-400 text-xs">Import CAD orders from StarGems portal</p>
              </div>
            </div>
            {step !== 'portals' && (
              <Button variant="outline" size="sm" onClick={() => setStep('portals')} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-7 text-xs">
                <X size={13} className="mr-1" /> Back to Portals
              </Button>
            )}
          </div>
        </header>

        <main className="max-w-[1400px] mx-auto p-6">

          {/* ===== PORTALS VIEW ===== */}
          {step === 'portals' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-base font-semibold">SGI Portal Connections</h2>
                <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-portal-btn">
                  <Plus size={14} className="mr-1" /> Add Portal
                </Button>
              </div>

              {showAddForm && (
                <form onSubmit={handleAddPortal} className="p-4 bg-white rounded-lg border border-teal-200 space-y-3" data-testid="add-portal-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Portal Name *</Label><Input value={portalForm.portal_name} onChange={e => setPortalForm(p => ({ ...p, portal_name: e.target.value }))} className="h-8 text-sm mt-1" data-testid="portal-name-input" /></div>
                    <div><Label className="text-xs">Portal URL</Label><Input value={portalForm.portal_url} onChange={e => setPortalForm(p => ({ ...p, portal_url: e.target.value }))} className="h-8 text-sm mt-1" /></div>
                    <div><Label className="text-xs">SGI Username *</Label><Input value={portalForm.username} onChange={e => setPortalForm(p => ({ ...p, username: e.target.value }))} className="h-8 text-sm mt-1" data-testid="portal-username-input" /></div>
                    <div><Label className="text-xs">SGI Password *</Label><Input type="password" value={portalForm.password} onChange={e => setPortalForm(p => ({ ...p, password: e.target.value }))} className="h-8 text-sm mt-1" data-testid="portal-password-input" /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-portal-btn">{saving ? 'Saving...' : 'Save Portal'}</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>Cancel</Button>
                  </div>
                </form>
              )}

              {loading ? <p className="text-sm text-muted-foreground text-center py-8">Loading...</p> : portals.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-lg border">
                  <Globe size={48} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground text-sm">No portals configured. Add your StarGems credentials to start importing.</p>
                </div>
              ) : portals.map(p => (
                <div key={p.id} className="bg-white rounded-lg border p-5 flex items-center justify-between" data-testid={`portal-card-${p.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center"><Globe size={22} className="text-teal-600" /></div>
                    <div>
                      <h3 className="font-semibold">{p.portal_name}</h3>
                      <p className="text-xs text-muted-foreground">{p.portal_url} &middot; User: {p.username}</p>
                      {p.last_import_at && <p className="text-[10px] text-muted-foreground mt-0.5">Last import: {new Date(p.last_import_at).toLocaleString()} &middot; {p.import_count} orders imported</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => handleFetchOrders(p.id)} disabled={fetching} className="bg-teal-600 hover:bg-teal-700" data-testid={`fetch-orders-${p.id}`}>
                      {fetching && activePortalId === p.id ? <Loader2 size={15} className="animate-spin mr-2" /> : <Download size={15} className="mr-2" />}
                      {fetching && activePortalId === p.id ? 'Fetching from SGI...' : 'Fetch Open Orders'}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { if (confirm('Delete?')) deletePortal(p.id).then(fetchPortals); }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== ORDER LIST (multi-select) ===== */}
          {step === 'list' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-base font-semibold">Open Orders from SGI</h2>
                  <p className="text-xs text-muted-foreground">{orderList.length} orders from Sketch Orders + CAD Request tabs. Select orders to import.</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-teal-600">{selectedOrders.size} selected</span>
                  <Button onClick={handleScrapeDetails} disabled={scraping || selectedOrders.size === 0} className="bg-teal-600 hover:bg-teal-700" data-testid="scrape-selected-btn">
                    {scraping ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                    {scraping ? 'Scraping Details...' : `Import ${selectedOrders.size} Selected`}
                  </Button>
                </div>
              </div>

              {/* Select all */}
              <div className="flex items-center gap-2 px-1">
                <button onClick={selectAll} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                  {selectedOrders.size === orderList.length ? <CheckSquare size={14} className="text-teal-600" /> : <Square size={14} />}
                  Select All
                </button>
              </div>

              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                      <th className="w-8 px-3 py-2"></th>
                      <th className="text-left px-3 py-2">Tab</th>
                      <th className="text-left px-3 py-2">Order #</th>
                      <th className="text-left px-3 py-2">Comp #</th>
                      <th className="text-left px-3 py-2">Notes</th>
                      <th className="text-left px-3 py-2">Order By</th>
                      <th className="text-left px-3 py-2">From</th>
                      <th className="text-center px-3 py-2">Days</th>
                      <th className="text-center px-3 py-2">Rush</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderList.map(o => (
                      <tr key={o.order_no} className={`border-b border-border/30 cursor-pointer transition-colors ${selectedOrders.has(o.order_no) ? 'bg-teal-50' : 'hover:bg-slate-50'}`} onClick={() => toggleOrder(o.order_no)} data-testid={`order-row-${o.order_no}`}>
                        <td className="px-3 py-2">
                          {selectedOrders.has(o.order_no) ? <CheckSquare size={14} className="text-teal-600" /> : <Square size={14} className="text-muted-foreground" />}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${o.tab === 'CAD Request' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{o.tab}</span>
                        </td>
                        <td className="px-3 py-2 font-mono font-bold">{o.order_no}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{o.comp_no || '\u2014'}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[300px] truncate">{o.notes || '\u2014'}</td>
                        <td className="px-3 py-2">{o.order_by}</td>
                        <td className="px-3 py-2 text-muted-foreground">{o.received_from}</td>
                        <td className="px-3 py-2 text-center">{o.days_since || '\u2014'}</td>
                        <td className="px-3 py-2 text-center">
                          {o.is_rush && <AlertTriangle size={14} className="text-red-500 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== DETAIL PREVIEW + REVIEW + EDIT ===== */}
          {step === 'details' && importSession && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-base font-semibold">Review & Import ({importSession.orders?.length} orders)</h2>
                  <p className="text-xs text-muted-foreground">Click any order to review and edit. Images downloaded from SGI. Select orders and confirm to create CAD tasks.</p>
                </div>
                <Button onClick={handleConfirmImport} disabled={confirming || selectedForImport.size === 0} className="bg-green-600 hover:bg-green-700" data-testid="confirm-import-btn">
                  {confirming ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                  {confirming ? 'Creating Tasks...' : `Create ${selectedForImport.size} CAD Tasks`}
                </Button>
              </div>

              {/* Order navigation bar */}
              <div className="flex gap-1 flex-wrap bg-white rounded-lg border p-2">
                {importSession.orders?.map((order, idx) => {
                  const selected = selectedForImport.has(idx);
                  const isActive = editingOrderIdx === idx;
                  return (
                    <button key={idx} onClick={() => { setEditingOrderIdx(idx); setExpandedOrder(idx); }}
                      className={`px-3 py-1.5 rounded text-xs font-mono transition-all flex items-center gap-1.5 ${isActive ? 'bg-slate-900 text-white' : selected ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`order-tab-${idx}`}>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedForImport(prev => { const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next; }); }}
                        className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${selected ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-400'}`}>
                        {selected && <Check size={8} />}
                      </button>
                      #{order.order_no}
                      {order.priority === 'urgent' && <AlertTriangle size={10} className="text-red-400" />}
                      {order.error && <X size={10} className="text-red-400" />}
                    </button>
                  );
                })}
              </div>

              {/* Active order detail view */}
              {editingOrderIdx !== null && importSession.orders?.[editingOrderIdx] && (() => {
                const order = importSession.orders[editingOrderIdx];
                const updateField = (field, value) => {
                  const updated = { ...importSession };
                  updated.orders = [...updated.orders];
                  updated.orders[editingOrderIdx] = { ...updated.orders[editingOrderIdx], [field]: value };
                  setImportSession(updated);
                };

                if (order.error) {
                  return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">Error scraping order #{order.order_no}: {order.error}</div>;
                }

                return (
                  <div className="bg-white rounded-lg border shadow-sm">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-base">Order #{order.order_no}</span>
                        {order.priority === 'urgent' && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded"><AlertTriangle size={10} /> RUSH</span>}
                        <span className="text-xs text-muted-foreground">by {order.order_by}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" disabled={editingOrderIdx === 0} onClick={() => { setEditingOrderIdx(editingOrderIdx - 1); setExpandedOrder(editingOrderIdx - 1); }} className="h-7 text-xs">Prev</Button>
                        <span className="text-xs text-muted-foreground">{editingOrderIdx + 1} / {importSession.orders.length}</span>
                        <Button size="sm" variant="ghost" disabled={editingOrderIdx >= importSession.orders.length - 1} onClick={() => { setEditingOrderIdx(editingOrderIdx + 1); setExpandedOrder(editingOrderIdx + 1); }} className="h-7 text-xs">Next</Button>
                      </div>
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Images */}
                      {order.images?.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-muted-foreground mb-2">REFERENCE IMAGES ({order.images.length})</h3>
                          <div className="flex gap-3 flex-wrap">
                            {order.images.map((img, i) => {
                              const imgSrc = img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
                              return (
                                <a key={i} href={imgSrc} target="_blank" rel="noreferrer" className="block">
                                  <img src={imgSrc} alt={`Ref ${i + 1}`} className="w-28 h-28 rounded-lg border-2 border-slate-200 object-cover hover:border-teal-400 hover:shadow-lg transition-all" onError={e => { e.target.style.display = 'none'; }} />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Editable fields grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { key: 'product_name', label: 'Product Name', span: 2 },
                          { key: 'piece_type', label: 'Piece Type' },
                          { key: 'size', label: 'Size' },
                          { key: 'metal_type', label: 'Metal Type' },
                          { key: 'metal_karat', label: 'Karat' },
                          { key: 'metal_colour', label: 'Metal Color' },
                          { key: 'gross_weight', label: 'Expected Weight (g)' },
                          { key: 'diamond_type', label: 'Diamond Type' },
                          { key: 'center_stone_dimensions', label: 'Center Stone Dims' },
                          { key: 'shank_width', label: 'Shank Width' },
                          { key: 'band_profile', label: 'Band Profile' },
                        ].map(({ key, label, span }) => (
                          <div key={key} className={span === 2 ? 'col-span-2' : ''}>
                            <Label className="text-[10px] text-muted-foreground">{label}</Label>
                            <Input value={order[key] || ''} onChange={e => updateField(key, e.target.value)}
                              className="h-7 text-xs mt-0.5" placeholder={label} data-testid={`edit-${key}-${editingOrderIdx}`} />
                          </div>
                        ))}
                      </div>

                      {/* Notes */}
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Notes / Instructions</Label>
                        <textarea value={order.notes || ''} onChange={e => updateField('notes', e.target.value)}
                          className="w-full mt-0.5 text-xs p-2 border rounded-md bg-slate-50 min-h-[60px] resize-y"
                          data-testid={`edit-notes-${editingOrderIdx}`} />
                      </div>

                      {/* Specs from portal */}
                      {order.specs_table && Object.keys(order.specs_table).length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-muted-foreground mb-1">PORTAL SPECS</h3>
                          <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 rounded p-2">
                            {Object.entries(order.specs_table).map(([k, v]) => (
                              <div key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-medium">{v}</span></div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

        </main>
      </div>
    </CollapsibleLayout>
  );
}
